import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrentPatientContext = createContext(null);

export const useCurrentPatient = () => {
  const context = useContext(CurrentPatientContext);
  if (!context) {
    throw new Error('useCurrentPatient must be used within a CurrentPatientProvider');
  }
  return context;
};

const SESSION_KEY = 'currentPatient';

export const CurrentPatientProvider = ({ children }) => {
  const [currentPatient, setCurrentPatientState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Persist to sessionStorage on change
  useEffect(() => {
    if (currentPatient) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(currentPatient));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [currentPatient]);

  // Clear on session expiry (same pattern as AuthContext)
  useEffect(() => {
    const handleSessionExpired = () => {
      setCurrentPatientState(null);
      sessionStorage.removeItem(SESSION_KEY);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const setCurrentPatient = useCallback((patient) => {
    if (!patient) {
      setCurrentPatientState(null);
      return;
    }
    setCurrentPatientState({
      id: patient.id,
      name: patient.name,
      nhi: patient.nhi,
      dateOfBirth: patient.dateOfBirth,
      phone: patient.phone,
      email: patient.email,
      address: patient.address
    });
  }, []);

  const clearCurrentPatient = useCallback(() => {
    setCurrentPatientState(null);
  }, []);

  const value = {
    currentPatient,
    setCurrentPatient,
    clearCurrentPatient
  };

  return (
    <CurrentPatientContext.Provider value={value}>
      {children}
    </CurrentPatientContext.Provider>
  );
};

export default CurrentPatientContext;
